import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, FileText, Sparkles } from "lucide-react";
import { toast } from "react-toastify";
import conferenceAndTradeMissionService from "../../../api/ConferenceAndTradeMission";
import { validateGhanaPhoneNumber, formatGhanaPhoneNumber } from "../../../utils/formValidators";
import ConferenceStepper from "../../../components/event_media/conference_registration/ConferenceStepper";
import Step1BasicInfo from "../../../components/event_media/conference_registration/Step1BasicInfo";
import Step2PersonalDetails from "../../../components/event_media/conference_registration/Step2PersonalDetails";
import Step3ContactDetails from "../../../components/event_media/conference_registration/Step3ContactDetails";
import Step4MotivationQuestions from "../../../components/event_media/conference_registration/Step4MotivationQuestions";
import StepPaymentChoice from "../../../components/event_media/conference_registration/StepPaymentChoice";

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

function getInitialFormData(selectedClient, selectedTicket) {
  const client = selectedClient?.client || selectedClient;
  const firstName = client?.firstName ?? (client?.first_name ?? "");
  const lastName = client?.lastName ?? (client?.last_name ?? "");
  const fullName = client?.fullName ?? ([firstName, lastName].filter(Boolean).join(" ") || "");
  const parts = fullName ? fullName.trim().split(/\s+/) : [];
  const f = parts[0] || firstName;
  const l = parts.length > 1 ? parts.slice(1).join(" ") : lastName;
  return {
    firstName: f || "",
    lastName: l || "",
    nationality: "",
    email: client?.email ?? "",
    gender: "",
    dateOfBirth: "",
    profession: "",
    organization: "",
    countryOfResidence: "",
    departureCity: "",
    profilePhoto: null,
    contactNumber: client?.phone ?? client?.phone_number ?? "",
    address: client?.address ?? client?.region ?? "",
    motivationAnswers: [],
    selectedTicket: selectedTicket || null,
  };
}

export default function AgentConferenceRegistration({ eventData, slug, selectedClient, selectedTicket, quantity = 1, onBack }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(() => getInitialFormData(selectedClient, selectedTicket));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [showPaymentChoice, setShowPaymentChoice] = useState(false);

  useEffect(() => {
    if (selectedTicket) {
      setFormData((prev) => ({ ...prev, selectedTicket }));
    }
  }, [selectedTicket]);

  const hasMotivationQuestions = eventData?.motivational_questions?.length > 0;
  const totalSteps = hasMotivationQuestions ? 4 : 3;

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 0) {
      if (!formData.firstName?.trim()) newErrors.firstName = "First name is required";
      if (!formData.lastName?.trim()) newErrors.lastName = "Last name is required";
      if (!formData.nationality?.trim()) newErrors.nationality = "Nationality is required";
      if (!formData.email?.trim()) {
        newErrors.email = "Email address is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
    }
    if (step === 1) {
      if (!formData.gender?.trim()) newErrors.gender = "Gender is required";
      if (!formData.dateOfBirth?.trim()) newErrors.dateOfBirth = "Date of birth is required";
      if (!formData.profession?.trim()) newErrors.profession = "Profession is required";
      if (!formData.organization?.trim()) newErrors.organization = "Organization/University is required";
      if (!formData.countryOfResidence?.trim()) newErrors.countryOfResidence = "Country of residence is required";
      if (!formData.departureCity?.trim()) newErrors.departureCity = "Departure city is required";
    }
    if (step === 2) {
      const contactValidation = validateGhanaPhoneNumber(formData.contactNumber?.replace(/\s/g, ""));
      if (!formData.contactNumber?.trim()) {
        newErrors.contactNumber = "Contact number is required";
      } else if (!contactValidation.isValid) {
        newErrors.contactNumber = contactValidation.message;
      }
      if (!formData.address?.trim()) newErrors.address = "Address is required";
    }
    if (step === 3 && hasMotivationQuestions) {
      (eventData?.motivational_questions || []).forEach((q, i) => {
        const answer = formData.motivationAnswers?.[i];
        if (!answer || (typeof answer === "string" && !answer.trim())) {
          newErrors[`motivation_${i}`] = "This question is required";
        }
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Builds conference registration payload (agent registering for client/student).
   * See PAYLOAD STRUCTURE below for backend contract.
   */
  const transformFormDataToPayload = async () => {
    if (!formData.selectedTicket) {
      toast.info("Please select a ticket category to continue");
      throw new Error("Missing selectedTicket");
    }
    let profilePhotoBase64 = null;
    if (formData.profilePhoto instanceof File) {
      try {
        profilePhotoBase64 = await fileToBase64(formData.profilePhoto);
      } catch (err) {
        toast.error("Failed to process profile photo. Please try again.");
        throw err;
      }
    } else if (typeof formData.profilePhoto === "string") {
      profilePhotoBase64 = formData.profilePhoto;
    }
    const client = selectedClient?.client || selectedClient;
    const qty = Math.max(1, Number(quantity) || 1);
    const payload = {
      event_slug: slug,
      event_type: "conference",
      ticket_type: formData.selectedTicket,
      quantity: qty,
      total_amount: (formData.selectedTicket?.price ?? 0) * qty,
      first_name: formData.firstName,
      last_name: formData.lastName,
      nationality: formData.nationality,
      email: formData.email,
      gender: formData.gender,
      date_of_birth: formData.dateOfBirth,
      profession: formData.profession,
      organization: formData.organization,
      country_of_residence: formData.countryOfResidence,
      departure_city: formData.departureCity,
      contact_number: formatGhanaPhoneNumber(formData.contactNumber?.replace(/\s/g, "")),
      address: formData.address,
      profile_photo: profilePhotoBase64,
      client_slug: client?.slug ?? client?.id ?? "",
      client_type: selectedClient?.clientType ?? "clients",
      client_data: {
        client_slug: client?.slug ?? client?.id ?? "",
        client_type: selectedClient?.clientType ?? "clients",
      },
    };
    const motivationalQuestions = eventData?.motivational_questions || [];
    payload.motivation_answers =
      motivationalQuestions.length > 0 && formData.motivationAnswers
        ? motivationalQuestions.map((q, i) => ({
            question: q.question,
            answer: formData.motivationAnswers[i],
          }))
        : [];
    return payload;
  };

  const handleFormSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = await transformFormDataToPayload();
      const response = await conferenceAndTradeMissionService.createAgentEvent({
        ...payload,
        motivation_answers: payload.motivation_answers || [],
      });
      const ok = response.status === true || response.data?.status_code === 200;
      const data = response.data ?? response.data?.data;
      if (ok) {
        toast.success(response.message || response.data?.reason || "Registration submitted successfully!");
        setRegistrationData(data);
        setShowPaymentChoice(true);
        setCurrentStep(hasMotivationQuestions ? 4 : 3);
      } else {
        toast.error(response.message || response.data?.reason || "Failed to submit registration. Please try again.");
      }
    } catch (err) {
      toast.error(err?.message || "An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      const finalStep = hasMotivationQuestions ? 3 : 2;
      if (currentStep === finalStep && !showPaymentChoice) {
        handleFormSubmit();
      } else {
        setCurrentStep(currentStep + 1);
      }
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  const handleBack = () => {
    if (currentStep > 0 && !showPaymentChoice) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    } else if (currentStep === 0 && onBack) {
      onBack();
    }
  };

  const handlePayLater = () => {
    navigate(`/event-media-sales/event-details/${slug}/registration-success`, {
      state: {
        registrationSuccess: true,
        applicationId: registrationData?.application_id || registrationData?.id,
        registrationData,
        eventData,
        payLater: true,
      },
    });
    toast.success("Registration completed. Client can pay later.");
  };

  const handlePayNow = () => {
    navigate(`/event-media-sales/event-details/${slug}/registration-success`, {
      state: {
        registrationData,
        eventData,
        slug,
        selectedTicket: formData.selectedTicket,
        applicationId: registrationData?.application_id || registrationData?.id,
        payNow: true,
      },
    });
    toast.info("Redirecting to success page.");
  };

  const handleClose = () => {
    if (onBack) onBack();
    else navigate(`/event-media-sales/event-details/${slug}`);
  };

  const renderStep = () => {
    if (showPaymentChoice) {
      return (
        <StepPaymentChoice
          formData={formData}
          setFormData={setFormData}
          onPayNow={handlePayNow}
          onPayLater={handlePayLater}
        />
      );
    }
    switch (currentStep) {
      case 0:
        return <Step1BasicInfo formData={formData} setFormData={setFormData} errors={errors} />;
      case 1:
        return <Step2PersonalDetails formData={formData} setFormData={setFormData} errors={errors} />;
      case 2:
        return <Step3ContactDetails formData={formData} setFormData={setFormData} errors={errors} />;
      case 3:
        if (hasMotivationQuestions) {
          return (
            <Step4MotivationQuestions
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              motivationalQuestions={eventData.motivational_questions}
            />
          );
        }
        return null;
      default:
        return null;
    }
  };

  const showBackButton = currentStep === 0 ? (
    <motion.button
      type="button"
      onClick={handleClose}
      whileHover={{ x: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="flex gap-2 items-center self-start px-5 py-2.5 text-sm font-semibold text-white bg-white/20 backdrop-blur-sm rounded-xl border border-white/30"
    >
      <ChevronLeft size={18} className="text-white" />
      Back
    </motion.button>
  ) : (
    <motion.button
      type="button"
      onClick={handleBack}
      disabled={showPaymentChoice}
      whileHover={!showPaymentChoice ? { scale: 1.02 } : {}}
      whileTap={!showPaymentChoice ? { scale: 0.98 } : {}}
      className={"px-6 py-3 text-sm font-semibold rounded-lg transition-all " + (currentStep === 0 || showPaymentChoice ? "text-gray-400 bg-gray-100 cursor-not-allowed" : "text-gray-700 bg-gray-100 hover:bg-gray-200")}
    >
      Back
    </motion.button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      <div className="overflow-hidden sticky top-16 z-40 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 shadow-lg lg:top-20">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="relative px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
              {showBackButton}
              <div className="flex flex-col gap-2 items-center text-center sm:flex-1">
                <div className="flex gap-2 items-center">
                  <div className="flex justify-center items-center w-10 h-10 text-orange-600 bg-white rounded-xl shadow-lg">
                    <FileText size={20} />
                  </div>
                  <h1 className="text-2xl font-bold text-white sm:text-3xl">Conference Registration (for client)</h1>
                </div>
                <p className="text-sm text-orange-100 sm:text-base">Complete the form on behalf of your client</p>
              </div>
              <div className="hidden justify-center items-center w-12 h-12 sm:flex text-white/20">
                <Sparkles size={24} />
              </div>
            </div>
            <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="overflow-hidden mt-6 h-1 rounded-full bg-white/20">
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: (currentStep + 1) / (totalSteps + 1) }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="h-full bg-white rounded-full shadow-lg"
                style={{ transformOrigin: "left" }}
              />
            </motion.div>
          </div>
        </div>
      </div>
      <div className="px-4 py-8 pb-28 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8">
            <ConferenceStepper currentStep={currentStep} hasMotivationQuestions={hasMotivationQuestions} showPaymentChoice={showPaymentChoice} />
          </div>
          <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
        </div>
      </div>

      {!showPaymentChoice && (
        <div className="fixed bottom-0 left-0 right-0 z-30 px-4 py-4 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] sm:px-6">
          <div className="mx-auto flex max-w-7xl justify-between items-center gap-4">
            <motion.button
              type="button"
              onClick={currentStep === 0 ? handleClose : handleBack}
              disabled={showPaymentChoice}
              whileHover={!showPaymentChoice ? { scale: 1.02 } : {}}
              whileTap={!showPaymentChoice ? { scale: 0.98 } : {}}
              className={"shrink-0 px-6 py-3 text-sm font-semibold rounded-lg transition-all " + (currentStep === 0 || showPaymentChoice ? "text-gray-400 bg-gray-100 cursor-not-allowed" : "text-gray-700 bg-gray-100 hover:bg-gray-200")}
            >
              {currentStep === 0 ? "Back to Event" : "Back"}
            </motion.button>
            <motion.button
              type="button"
              onClick={handleNext}
              disabled={isSubmitting}
              whileHover={!isSubmitting ? { scale: 1.02 } : {}}
              whileTap={!isSubmitting ? { scale: 0.98 } : {}}
              className="shrink-0 px-8 py-3 text-base font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-md transition-all hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex gap-2 items-center">
                  <span className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
                  Submitting...
                </span>
              ) : (hasMotivationQuestions ? currentStep === 3 : currentStep === 2) ? (
                "Submit Registration"
              ) : (
                "Continue"
              )}
            </motion.button>
          </div>
        </div>
      )}
    </div>
  );
}
